import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { debounce, mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const QUICK_ADD_REQUEST_TIMEOUT = 10000;
const QUICK_ADD_CONTENT_STALE = Symbol('quick-add-content-stale');

/**
 * Prevents an extension-blocked dynamic import from leaving Quick Add busy
 * indefinitely. The underlying module load is not retried or cancelled.
 * @template T
 * @param {Promise<T>} promise
 * @param {string} message
 * @returns {Promise<T>}
 */
function settleQuickAddWork(promise, message) {
  let timeout;

  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timeout = setTimeout(() => reject(new Error(message)), QUICK_ADD_REQUEST_TIMEOUT);
    }),
  ]).finally(() => clearTimeout(timeout));
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null | typeof QUICK_ADD_CONTENT_STALE>>} */
  #pendingContent = new Map();
  /** @type {number} */
  #cacheGeneration = 0;
  /** @type {boolean} */
  #isOpening = false;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productPageUrl = productCard?.productPageUrl;
    if (!productPageUrl) return null;

    return new URL(productPageUrl).searchParams.get('variant');
  }

  /**
   * Checks the already-rendered variant data so a dated variant cannot bypass the Quick Add disclosure.
   * @param {string | null} [selectedVariantId] - Variant to check; defaults to the card's selected variant.
   * @returns {boolean}
   */
  #selectedVariantHasEstimatedShipDate(selectedVariantId = this.#getSelectedVariantId()) {
    if (!selectedVariantId) return false;

    /** @type {(HTMLElement & { hasEstimatedShipDate?: (variantId: string) => boolean }) | null} */
    const estimatedShipDate = this.querySelector('estimated-ship-date');
    if (!estimatedShipDate) return false;

    if (typeof estimatedShipDate.hasEstimatedShipDate === 'function') {
      return estimatedShipDate.hasEstimatedShipDate(selectedVariantId);
    }

    const dataScript = estimatedShipDate.querySelector('[data-estimated-ship-date-data]');
    if (!dataScript?.textContent) return false;

    try {
      const data = JSON.parse(dataScript.textContent);
      const value = data?.variants?.[selectedVariantId]?.estimated_ship_date;
      return typeof value === 'string' && value.trim() !== '';
    } catch {
      return false;
    }
  }

  /**
   * Removes product-page-only UI before fetched content enters the Quick Add dialog.
   * @param {Element} productGrid
   */
  #removeProductPageOnlyContent(productGrid) {
    const productPageOnlyElements = productGrid.querySelectorAll('[data-product-page-only]');

    for (const element of productPageOnlyElements) {
      element.remove();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.cancelPrefetchProduct();
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;
    const productOptionsCount = this.dataset.productOptionsCount;

    // Multi-option products always use the fetched Quick Add dialog and do not
    // need an estimated-shipping payload embedded in their collection card.
    if (productOptionsCount !== '1') {
      this.setAttribute('data-quick-add-button', 'choose');
      return;
    }

    // A sold-out swatch should lead to the picker rather than reveal a disabled Add button.
    if (this.#isSelectedVariantUnavailable()) {
      this.setAttribute('data-quick-add-button', 'choose');
      return;
    }

    const requestedVariantId = this.#getSelectedVariantId();
    /** @type {(HTMLElement & { refresh?: () => void }) | null} */
    const estimatedShipDate = this.querySelector('estimated-ship-date');

    // Gift cards and products that explicitly hide shipping dates do not render
    // the helper and can retain the normal direct-add behavior.
    if (!estimatedShipDate) {
      this.setAttribute('data-quick-add-button', 'add');
      return;
    }

    // Keep the safe disclosure path visible while the selected swatch response
    // is in flight. The response carries the compact payload for that variant.
    this.setAttribute('data-quick-add-button', 'choose');

    event.promise
      ?.then(({ detail }) => {
        if (!this.isConnected) return;

        const responseVariantId = detail?.resource?.id ? String(detail.resource.id) : null;
        const currentVariantId = this.#getSelectedVariantId();

        if (!responseVariantId || currentVariantId !== responseVariantId || requestedVariantId !== responseVariantId) {
          return;
        }

        const replacementComponents = detail.html?.querySelectorAll(
          'estimated-ship-date[data-attach-only="true"]'
        );
        const replacement = Array.from(replacementComponents || []).find(
          (component) =>
            component instanceof HTMLElement &&
            component.dataset.productId === estimatedShipDate.dataset.productId &&
            component.dataset.initialVariantId === responseVariantId
        );

        // If the response did not contain the compact payload, retain the safer
        // dialog path instead of allowing a dated variant to add directly.
        if (!(replacement instanceof HTMLElement)) return;

        morph(estimatedShipDate, replacement);
        estimatedShipDate.refresh?.();

        const selectedVariantHasEstimatedShipDate = this.#selectedVariantHasEstimatedShipDate(responseVariantId);
        this.setAttribute('data-quick-add-button', selectedVariantHasEstimatedShipDate ? 'choose' : 'add');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Product select event rejected:', error);
      });
  };

  /** @returns {boolean} Whether the selected card swatch is explicitly unavailable. */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = (event) => {
    this.#invalidateContentCache();

    const eventPromise = event?.promise;
    if (eventPromise && typeof eventPromise.then === 'function') {
      void Promise.resolve(eventPromise).then(
        () => this.#invalidateContentCache(),
        () => this.#invalidateContentCache()
      );
    }
  };

  #invalidateContentCache() {
    this.#cacheGeneration += 1;
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  }

  /**
   * Fetches and parses product content once for both intent prefetching and clicks.
   * @param {string} productPageUrl - The product page URL to load
   * @returns {Promise<Element | null | typeof QUICK_ADD_CONTENT_STALE>}
   */
  #loadProductGrid(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const cachedContent = this.#cachedContent.get(productPageUrl);
    if (cachedContent) return Promise.resolve(cachedContent);

    const pendingContent = this.#pendingContent.get(productPageUrl);
    if (pendingContent) return pendingContent;

    const cacheGeneration = this.#cacheGeneration;
    const request = this.fetchProductPage(productPageUrl)
      .then((html) => {
        const productGrid = html?.querySelector('[data-product-grid-content]');
        if (!productGrid) return null;

        const cachedProductGrid = /** @type {Element} */ (productGrid.cloneNode(true));
        this.#removeProductPageOnlyContent(cachedProductGrid);
        if (cacheGeneration !== this.#cacheGeneration) return QUICK_ADD_CONTENT_STALE;
        this.#cachedContent.set(productPageUrl, cachedProductGrid);

        return cachedProductGrid;
      })
      .catch((error) => {
        console.warn('[quick-add] Unable to load product content:', error);
        return null;
      })
      .finally(() => {
        if (this.#pendingContent.get(productPageUrl) === request) {
          this.#pendingContent.delete(productPageUrl);
        }
      });

    this.#pendingContent.set(productPageUrl, request);

    return request;
  }

  /**
   * Prefetches after a short hover dwell to avoid requests from incidental pointer movement.
   */
  prefetchProduct = debounce(() => {
    void this.#loadProductGrid(this.productPageUrl);
  }, 150);

  /**
   * Cancels a prefetch that has not started yet.
   */
  cancelPrefetchProduct = () => {
    this.prefetchProduct.cancel();
  };

  /**
   * Starts prefetching immediately for keyboard focus and pointer press.
   */
  prefetchProductImmediate = () => {
    this.cancelPrefetchProduct();
    void this.#loadProductGrid(this.productPageUrl);
  };

  /**
   * Sets immediate visual and assistive-technology feedback while Quick Add opens.
   * @param {HTMLButtonElement | null} button - The activated Quick Add button
   * @param {boolean} isLoading - Whether the product content is loading
   */
  #setLoadingState(button, isLoading) {
    if (!button) return;

    button.toggleAttribute('data-loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  }

  /**
   * Turns an unavailable Quick Add dialog into a safe product-page fallback.
   * @param {HTMLButtonElement | null} button - The activated Quick Add button
   */
  #showProductPageFallback(button) {
    if (!button) return;

    const errorMessage =
      Theme.translations?.quick_add_load_error || 'Product options could not be loaded.';
    const viewProductText = Theme.translations?.view_product || 'View product';
    const productTitle = this.dataset.productTitle?.trim();
    const errorTarget = this.querySelector('[ref="addToCartTextError"]');
    const liveRegion = this.querySelector('[ref="liveRegion"]');

    if (errorTarget instanceof HTMLElement) {
      errorTarget.classList.remove('hidden');

      const existingText = errorTarget.childNodes[2];
      if (existingText instanceof Text) {
        existingText.textContent = errorMessage;
      } else {
        errorTarget.appendChild(document.createTextNode(errorMessage));
      }
    }

    button.textContent = viewProductText;
    button.style.paddingInline = 'var(--padding-sm)';
    button.dataset.quickAddFallback = 'true';
    button.setAttribute('aria-label', productTitle ? `${viewProductText}: ${productTitle}` : viewProductText);
    button.setAttribute('title', errorMessage);
    button.removeAttribute('aria-haspopup');
    button.removeAttribute('aria-expanded');

    if (liveRegion instanceof HTMLElement) {
      liveRegion.textContent = errorMessage;
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#isOpening) return;

    const currentUrl = this.productPageUrl;
    const eventTarget = event.target;
    const button =
      eventTarget instanceof HTMLButtonElement
        ? eventTarget
        : /** @type {HTMLButtonElement | null} */ (this.querySelector('.quick-add__button--choose'));

    if (this.dataset.usesSellingPlans === 'true' || button?.dataset.quickAddFallback === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.cancelPrefetchProduct();
    this.#isOpening = true;
    this.#setLoadingState(button, true);

    try {
      let productGrid = await this.#loadProductGrid(currentUrl);
      if (productGrid === QUICK_ADD_CONTENT_STALE) {
        productGrid = await this.#loadProductGrid(currentUrl);
      }

      if (productGrid === QUICK_ADD_CONTENT_STALE) return;
      if (!productGrid) {
        if (this.isConnected && currentUrl && currentUrl === this.productPageUrl) {
          this.#showProductPageFallback(button);
        }
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      this.#openQuickAddModal(button);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[quick-add] Unable to open product options:', error);
      }

      if (this.isConnected && currentUrl && currentUrl === this.productPageUrl) {
        this.#showProductPageFallback(button);
      }
    } finally {
      this.#setLoadingState(button, false);
      this.#isOpening = false;
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /** @param {HTMLButtonElement | null} button */
  #openQuickAddModal = (button) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    // REAL: Safari pointer clicks can leave the containing tabpanel focused.
    // Seed the native dialog's return focus with its actual opener instead.
    button?.focus({ preventScroll: true });
    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    let didTimeOut = false;
    const timeout = window.setTimeout(() => {
      didTimeOut = true;
      abortController.abort();
    }, QUICK_ADD_REQUEST_TIMEOUT);

    try {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      if (!html.querySelector('[data-product-grid-content]')) {
        throw new Error('Product page response did not include Quick Add content');
      }

      return html;
    } catch (error) {
      if (didTimeOut) {
        throw new Error('Quick Add product request timed out');
      }

      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      window.clearTimeout(timeout);

      if (this.#abortController === abortController) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent =
      productDetails?.querySelector('.buy-buttons-block product-form-component') ||
      productGrid.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form[action*="/cart/add"]');
    const estimatedShipDate = productDetails?.querySelector(
      'estimated-ship-date:not(.estimated-ship-date--attach-only)'
    );

    // Move the visible estimate while the fetched content is detached. This keeps it next to the
    // purchase form on desktop and prevents Horizon's mobile layout reduction from discarding it.
    if (estimatedShipDate && productFormComponent && productForm) {
      productFormComponent.insertBefore(estimatedShipDate, productForm);
    }

    if (isMobileBreakpoint()) {
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      // REAL: keep the details label inside the existing variant-aware title link.
      const fullDetailsLink = productDetails?.querySelector('.real-quick-add-details-link');
      if (fullDetailsLink) {
        productTitle.appendChild(fullDetailsLink);
      }
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    // Product pages can contain optional components that are not present on the
    // collection page. Load their modules before inserting the fetched markup.
    const componentLoad = globalThis.ThemeComponentLoader?.load(productGrid);
    if (componentLoad) {
      const componentLoadsSucceeded = await settleQuickAddWork(
        componentLoad,
        'Quick Add component loading timed out'
      );
      if (componentLoadsSucceeded.some((succeeded) => !succeeded)) {
        throw new Error('Quick Add component loading failed');
      }
    }
    morph(modalContent, productGrid);

    /** @type {(HTMLElement & { refresh?: () => void }) | null} */
    const estimatedShipDateComponent = modalContent.querySelector('estimated-ship-date');
    estimatedShipDateComponent?.refresh?.();

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
